import { aboutInfo, faqData } from '@/mocks/videoData';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useState } from 'react';

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-background-200/70 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left bg-background-50 hover:bg-background-100 transition-colors"
      >
        <span className="text-sm font-medium text-foreground-900 pr-4">{question}</span>
        <div className={`flex-shrink-0 w-6 h-6 flex items-center justify-center transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>
          <i className="ri-arrow-down-s-line text-foreground-500"></i>
        </div>
      </button>
      {open && (
        <div className="px-5 pb-4 pt-0">
          <p className="text-sm text-foreground-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('company_alt') as string;

    if (honeypot && honeypot.trim()) {
      setFormState('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    setFormState('submitting');

    try {
      const res = await fetch('https://form.readdy.ai/api/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }

      if (res.ok && (parsed as { code?: string })?.code === 'OK') {
        setFormState('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        setFormState('error');
        const msg = (parsed as { meta?: { message?: string } })?.meta?.message || '提交失败，请稍后重试';
        setFormMessage(msg);
      }
    } catch {
      setFormState('error');
      setFormMessage('网络错误，请稍后重试');
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: `关于我们 - ${aboutInfo.brandName}`,
            description: aboutInfo.intro,
            url: 'https://fulishipin.com/about',
            about: {
              '@type': 'Organization',
              name: aboutInfo.brandName,
              description: aboutInfo.tagline,
              url: 'https://fulishipin.com',
              contactPoint: {
                '@type': 'ContactPoint',
                email: aboutInfo.contact.email,
                contactType: 'customer service',
              },
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqData.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
              },
            })),
          }),
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="max-w-5xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

          {/* Brand Intro */}
          <section className="mb-12 md:mb-16">
            <div className="text-center mb-10">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">关于{aboutInfo.brandName}</h1>
              <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto">{aboutInfo.tagline}</p>
            </div>

            <div className="max-w-3xl mx-auto">
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">{aboutInfo.intro}</p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">{aboutInfo.mission}</p>
            </div>
          </section>

          {/* Features */}
          <section className="mb-12 md:mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">我们的优势</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {aboutInfo.features.map((feat) => (
                <div key={feat.title} className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 mb-3">
                    <i className="ri-check-line text-primary-600"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">{feat.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{feat.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Stats */}
          <section className="mb-12 md:mb-16 bg-background-900 rounded-xl p-8 md:p-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {aboutInfo.stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-background-700/50">
                    <i className={`${stat.icon} text-xl text-primary-400`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-background-50">{stat.value}</div>
                  <div className="text-sm text-background-400 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-12 md:mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">常见问题</h2>
            <div className="max-w-2xl mx-auto space-y-3">
              {faqData.map((faq) => (
                <FAQItem key={faq.question} question={faq.question} answer={faq.answer} />
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="mb-12">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">联系我们</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                    <i className="ri-mail-line text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">电子邮箱</p>
                    <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100">
                    <i className="ri-wechat-line text-lg text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">微信</p>
                    <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.wechat}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-100">
                    <i className="ri-map-pin-line text-lg text-secondary-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">地址</p>
                    <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.address}</p>
                  </div>
                </div>
              </div>

              <div className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                <h3 className="text-base font-semibold text-foreground-900 mb-4">给我们留言</h3>
                <form onSubmit={handleSubmit} className="space-y-3">
                  <input
                    type="text"
                    name="name"
                    placeholder="您的姓名"
                    required
                    className="w-full h-10 px-3 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="您的邮箱"
                    required
                    className="w-full h-10 px-3 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400"
                  />
                  <textarea
                    name="message"
                    placeholder="请输入留言内容..."
                    rows={4}
                    maxLength={500}
                    required
                    className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 resize-none"
                  ></textarea>
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <button
                    type="submit"
                    disabled={formState === 'submitting'}
                    className="w-full h-10 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-background-50 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {formState === 'submitting' ? '提交中...' : '提交留言'}
                  </button>
                  {formState === 'success' && (
                    <p className="text-xs text-accent-600 text-center">{formMessage}</p>
                  )}
                  {formState === 'error' && (
                    <p className="text-xs text-red-500 text-center">{formMessage}</p>
                  )}
                </form>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}